import { useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Typography, Fade, makeStyles } from '@material-ui/core';
import CustomSwitch from '../../customSwitch';
import Tooltip from '../../tooltip';
import { ReactComponent as ErrorIcon } from '../../../../assets/icons/warning-triangle.svg';
import { CYCLE_STEP_TYPES } from '../../../../constants/cycle';
import { formatDate } from '../../../../utility/dateUtils';

const useStyles = makeStyles(({ palette: { primary }, spacing }) => ({
  root: {
    backgroundColor: primary.white,
    boxSizing: 'border-box',
    border: `1px solid ${primary.bluish5}`,
    borderRadius: 8,
    cursor: 'default',
    display: 'grid',
    gridTemplateColumns: 'fit-content(0) 1fr fit-content(0) fit-content(0)',
    alignItems: 'center',
    gap: 8,
    padding: spacing(2, 4, 2, 2),
    transition: 'border-color .3s ease',
    height: 52,
    '&:hover': {
      borderColor: primary.bluish1,
    },
    '&$selected:hover': {
      borderColor: primary.blue1,
    },
  },
  selected: {
    borderColor: primary.blue1,
    padding: spacing(2, 4, 2, 2),
  },
  selectable: {
    cursor: 'pointer',
  },
  name: {
    userSelect: 'none',
  },
  switch: {
    width: 32,
  },
  dateWrapper: {
    display: 'flex',
    alignItems: 'center',
  },
  placeholderDate: {
    color: primary.bluish5,
  },
  icon: {
    width: 24,
    height: 24,
  },
}));

const CycleStep = ({
  translations,
  step,
  startAt = undefined,
  endAt = undefined,
  errorMessage = '',
  error = null,
  isSelected,
  onSelect,
  onToggleStep,
}) => {
  const classes = useStyles();

  const { id, icon: StepIcon, name, isActive } = step;

  const hasError = useMemo(() => {
    if (!error) return false;

    return Object.values(error).some(val => {
      if (!val) return false;
      return true;
    });
  }, [error]);

  return (
    <div
      className={classNames(classes.root, {
        [classes.selected]: isSelected,
        [classes.selectable]: isActive,
      })}
      onClick={onSelect}
    >
      <StepIcon className={classes.icon} />
      <div>
        <Typography className={classes.name} variant="subtitle1">
          {name}
        </Typography>
        <div className={classes.dateWrapper}>
          {isActive ? (
            <Typography variant="body2">
              {id === CYCLE_STEP_TYPES.EMAIL_STEP
                ? formatDate(startAt, 'LLL dd, yyyy')
                : `${formatDate(startAt, 'LLL dd')} - ${formatDate(
                    endAt,
                    'LLL dd, yyyy'
                  )}`}
            </Typography>
          ) : (
            <Typography className={classes.placeholderDate} variant="body2">
              {id === CYCLE_STEP_TYPES.EMAIL_STEP
                ? translations.start
                : `${translations.start} - ${translations.end}`}
            </Typography>
          )}
        </div>
      </div>
      <Fade in={hasError}>
        <div>
          <Tooltip text={errorMessage} icon={ErrorIcon} />
        </div>
      </Fade>
      <CustomSwitch
        className={classes.switch}
        tooltipText={
          isActive ? translations.tooltip.inactive : translations.tooltip.active
        }
        isChecked={isActive}
        onChange={onToggleStep}
      />
    </div>
  );
};

CycleStep.propTypes = {
  translations: PropTypes.object.isRequired,
  step: PropTypes.object.isRequired,
  startAt: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  endAt: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  errorMessage: PropTypes.string,
  error: PropTypes.object,
  isSelected: PropTypes.bool.isRequired,
  onToggleStep: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
};

export default CycleStep;
